<template>
  <div :class="[deviceType, 'progressContainer']">
    <Form
      ref="formRef"
      @submit="onSubmit"
      :validation-schema="schema"
      @invalid-submit="onInvalidSubmit"
    >
      <div :class="[deviceType, 'sub_container']">
        <div class="bottom_line">
          <div class="img_container">
            <img
              class="path-65 start_logo"
              src="../../../assets/icons/start_pic.svg"
            />
          </div>

          <h2 style="text-align: center">
            {{ t("ccOnBoarding.biometric.loginTitle") }}
          </h2>
          <div v-if="!hideField" :class="[deviceType, 'inputText']">
            <DropDown
              v-model="prop.biometricStartInfo.branch"
              :selectedOption="prop.biometricStartInfo.branch"
              :options="branchList"
              :value="branchList"
              :default="false"
              field="branch"
              :placeholder="t('ccOnBoarding.biometric.selectBranch')"
            />
          </div>
          <div v-if="!hideField" :class="[deviceType, 'inputText']">
            <InputText
              ref="idNoInput"
              class="inputcomponent"
              v-model="prop.biometricStartInfo.recruiterId"
              :value="prop.biometricStartInfo.recruiterId"
              :field="'recruiterId'"
              :required="false"
              :placeholder="t('ccOnBoarding.biometric.recruiterId')"
              :type="'text'"
              :inputmode="'text'"
              :max-length="10"
              :autocomplete="'new-password'"
              @keydown="handleNameKeyDown"
              @keydown.enter.prevent="handleEnterKey"
            />
          </div>
          <div v-if="!hideField" :class="[deviceType, 'inputText']">
            <DropDown
              v-model="prop.biometricStartInfo.applicationChannel"
              :selectedOption="prop.biometricStartInfo.applicationChannel"
              :options="channelList"
              :value="channelList"
              :default="false"
              field="applicationChannel"
              :placeholder="t('ccOnBoarding.biometric.applicationChannel')"
            />
          </div>
          <!-- overlay -->
          <div v-if="overlay" class="text-center flex-container">
            <br />
            <div class="spinner-border" role="status">
              <span class="sr-only">{{
                t("ccOnBoarding.uploadDocument.loading2")
              }}</span>
            </div>
            <br />
            <br />
            <p class="loading">
              <b>{{ t("ccOnBoarding.uploadDocument.validating") }}</b>
            </p>
            <br />
            <br />
          </div>
          <div v-if="isSubmitError" class="retry">
            <img
              class="path-65 retry_logo"
              src="../../../assets/icons/bio_timeout.svg"
            />
            <p>{{ t("ccOnBoarding.biometric.retryDesc") }}</p>
          </div>
        </div>
        <div :class="[deviceType, 'buttoncontainer']">
          <button
            v-if="!isSubmitError"
            :class="[deviceType, 'right-button']"
            @click="jump('start')"
            :disabled="isBtnDisabled"
          >
            {{ t("ccOnBoarding.resume.buttonStart") }}
          </button>
          <div :class="[deviceType, 'sub_buttoncontainer']">
            <button
              v-if="!isSubmitError"
              :class="[deviceType, 'left-button']"
              @click="jump('resume')"
              :disabled="isBtnDisabled"
            >
              {{ t("ccOnBoarding.resume.buttonResume") }}
            </button>
            <button
              v-if="!isSubmitError"
              :class="[deviceType, 'left-button']"
              @click="jump('documentResubmission')"
              :disabled="isBtnDisabled"
            >
              {{ t("ccOnBoarding.resume.buttonDocResubmission") }}
            </button>
          </div>
          <button
            v-if="isSubmitError"
            :class="[deviceType, 'right-button retry']"
            @click="retry()"
          >
            {{ t("ccOnBoarding.biometric.retry") }}
          </button>
        </div>
      </div>
    </Form>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  watchEffect,
  onBeforeMount,
  type PropType,
  onMounted,
  nextTick,
  type Ref,
} from "vue";
import { inject } from "vue";
import i18n from "@/plugin/i18n";
import MasterService from "@/service/restfulService/CCOnboardMasterService";
import DropDown from "./components/NewDropDownComponent.vue";
import InputText from "./components/NewInputTextComponent.vue";
import { Form } from "vee-validate";
import type BiometricStartInfo from "@/interface/BiometricStartInfo";

interface branchList {
  code: string;
  value: string;
  desc: string;
}

interface channelList {
  code: string;
  value: string;
}

const deviceType = inject("deviceType");
const { t } = i18n.global;
const recruiterEligible = ref("");
const formRef = ref();

const channelList: Ref<channelList[]> = ref([]);
const branchList: Ref<branchList[]> = ref([]);
const channel = ref("");
const overlay = ref(false);
const hideField = ref(false);
const isSubmit = ref(false);
const isSubmitError = ref(false);
const isBranch = ref("false");
const isChannel = ref("false");

const prop = defineProps({
  webFormRequestId: {
    type: String,
  },
  biometricStartInfo: {
    type: Object as PropType<BiometricStartInfo>,
    required: true,
  },
  token: {
    type: String,
    required: true,
  },
});

onBeforeMount(() => {
  MasterService.getChannelData()
    .then((res) => {
      channelList.value = res.data.applicationChannel;

      if (channelList.value.length > 0) {
        channelList.value.forEach((channel) => {
          if (channel.code == localStorage.getItem("applicationChannel")) {
            isChannel.value = "true";
          }
        });
      }
      if (isChannel.value == "false") {
        localStorage.setItem("applicationChannel", "");
      }
    })
    .catch((e) => {});

  const requestModel = {
    type: "STAFF",
  };

  MasterService.getBranchData(requestModel)
    .then((res) => {
      branchList.value = res.data.responseModel.branchList;

      if (branchList.value.length > 0) {
        branchList.value.forEach((branch) => {
          branch.value = branch.desc;
          if (branch.code == localStorage.getItem("branch")) {
            isBranch.value = "true";
          }
        });
      }

      if (isBranch.value == "false") {
        localStorage.setItem("branch", "");
      }
    })
    .catch((e) => {});

  prop.biometricStartInfo.recruiterId = localStorage.getItem("recruiter") || "";
  prop.biometricStartInfo.branch = localStorage.getItem("branch") || "";
  prop.biometricStartInfo.applicationChannel =
    localStorage.getItem("applicationChannel") || "";
});

const emit = defineEmits([
  "nextStep",
  "jumpStep",
  "submit",
  "overlay",
  "closeOverlay",
]);

const jump = (status: string) => {
  hideField.value = true;
  isSubmit.value = true;
  overlay.value = true;
  const requestModel = {
    recruiterCode: prop.biometricStartInfo.recruiterId,
  };
  MasterService.checkRecruiter(requestModel)
    .then((res) => {
      if (res.status == 200) {
        if (res.data.responseModel.recruiterEligible) {
          prop.biometricStartInfo.recruiterName =
            res.data.responseModel.recruiterName;
          if (prop.webFormRequestId) {
            const requestModel2 = {
              webFormRequestId: prop.webFormRequestId,
              applicationChannelCode:
                prop.biometricStartInfo.applicationChannel,
              recruiterId: prop.biometricStartInfo.recruiterId,
              branchCode: prop.biometricStartInfo.branch,
              recruiterName: prop.biometricStartInfo.recruiterName,
            };
            MasterService.biometricSubmitLogin(requestModel2, prop.token)
              .then((res) => {
                if (res.status == 200) {
                  hideField.value = false;
                  overlay.value = false;
                  isSubmit.value = false;
                  recruiterEligible.value = "true";
                  if (status == "documentResubmission") {
                    emit("jumpStep", 201, "", 4);
                  } else {
                    emit("jumpStep", 102, status, 4);
                  }
                }
              })
              .catch((e) => {
                // show retry button
                isSubmit.value = false;
                overlay.value = false;
                isSubmitError.value = true;
              });
          } else {
            if (status == "documentResubmission") {
              emit("jumpStep", 201, "", 4);
            } else {
              emit("jumpStep", 102, status, 4);
            }
          }
        } else {
          overlay.value = false;
          hideField.value = false;
          isSubmit.value = false;
          recruiterEligible.value = "false";
          invalidRecruiter();
        }
      }
    })
    .catch((e) => {
      isSubmit.value = false;
      overlay.value = false;
      isSubmitError.value = true;
    });
};

const onSubmit = () => {
  //   // emit("jumpStep", 1, "", 4);
  //   hideField.value = true;
  //   isSubmit.value = true;
  //   overlay.value = true;
  //   const requestModel = {
  //     recruiterCode: prop.biometricStartInfo.recruiterId,
  //   };
  //   MasterService.checkRecruiter(requestModel)
  //     .then((res) => {
  //       if (res.status == 200) {
  //         if (res.data.responseModel.recruiterEligible) {
  //           if (prop.webFormRequestId) {
  //             const requestModel2 = {
  //               webFormRequestId: prop.webFormRequestId,
  //               applicationChannelCode:
  //                 prop.biometricStartInfo.applicationChannel,
  //               recruiterId: prop.biometricStartInfo.recruiterId,
  //               branchCode: prop.biometricStartInfo.branch,
  //             };
  //             MasterService.biometricSubmitLogin(requestModel2, "")
  //               .then((res) => {
  //                 if (res.status == 200) {
  //                   hideField.value = false;
  //                   overlay.value = false;
  //                   isSubmit.value = false;
  //                   recruiterEligible.value = "true";
  //                   emit("jumpStep", 1, "", 4);
  //                 }
  //               })
  //               .catch((e) => {
  //                 // show retry button
  //                 isSubmit.value = false;
  //                 overlay.value = false;
  //                 isSubmitError.value = true;
  //               });
  //           } else {
  //             emit("jumpStep", 1, "", 4);
  //           }
  //         } else {
  //           overlay.value = false;
  //           hideField.value = false;
  //           isSubmit.value = false;
  //           recruiterEligible.value = "false";
  //           invalidRecruiter();
  //         }
  //       }
  //     })
  //     .catch((e) => {
  //       isSubmit.value = false;
  //       overlay.value = false;
  //       isSubmitError.value = true;
  //     });
};

const retry = () => {
  hideField.value = false;
  isSubmitError.value = false;
};

const onInvalidSubmit = () => {};

async function invalidRecruiter() {
  // Wait for the reactive schema (driven by recruiterEligible) to propagate to
  // the Form, then explicitly validate the field. This is required for the
  // recruiterId loaded from localStorage, since the field was never focused/
  // blurred by the user and would otherwise stay unvalidated (no error shown).
  await nextTick();
  formRef.value?.validateField("recruiterId");
}

const schema = computed(() => {
  return {
    recruiterId:
      recruiterEligible.value != "false" ? "required" : "invalidRecruiterId",
    applicationChannel: "required",
    branch: "required",
  };
});

const isBtnDisabled = computed(() => {
  if (
    prop.biometricStartInfo.recruiterId.length == 0 ||
    prop.biometricStartInfo.branch.length == 0 ||
    prop.biometricStartInfo.applicationChannel.length == 0
  ) {
    return true;
  } else {
    return isSubmit.value;
  }
});

watch(
  () => prop.biometricStartInfo.recruiterId,
  (newValue) => {
    if (newValue) {
      localStorage.setItem("recruiter", newValue);
    }
  }
);

watch(
  () => prop.biometricStartInfo.applicationChannel,
  (newValue) => {
    if (newValue) {
      localStorage.setItem("applicationChannel", newValue);
    }
  }
);

watch(
  () => prop.biometricStartInfo.branch,
  (newValue) => {
    if (newValue) {
      localStorage.setItem("branch", newValue);
    }
  }
);

const handleNameKeyDown = (event: KeyboardEvent) => {
  // const isSymbol = /[^\sa-zA-Z_]/.test(event.key);
  const isSymbol = /^[a-zA-Z0-9]+$/.test(event.key);
  const isDeleteKey = event.key === "Delete";
  const isBackspace = event.key === "Backspace";
  const isTabKey = event.key === " Tab";
  const isArrowKey = /^Arrow(Left|Right)$/.test(event.key);

  const isIOSArrowKey =
    event.key === "UIKeyInputLeftArrow" || event.key === "UIKeyInputRightArrow";
  const isAndroidArrowKey =
    event.key === "ArrowLeft" || event.key === "ArrowRight";

  if (
    !isSymbol &&
    !isBackspace &&
    !(isArrowKey || isIOSArrowKey || isAndroidArrowKey) &&
    !isDeleteKey &&
    !isTabKey
  ) {
    event.preventDefault();
  }
  recruiterEligible.value = "true";
};

const handleEnterKey = () => {};
</script>

<style scoped>
.img_container {
  border-radius: 8px;
  position: relative;
}

h2 {
  color: var(--Web-Black-High, #212121);

  /* Custom 3 */
  font-family: Outfit;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 31.2px */
  margin: 24px 0 16px 0;
}

.buttoncontainer {
  align-items: center;
  margin-top: 24px;
  gap: 16px;
}

/* .buttoncontainer:is(.Desktop, .Tablet) { */
/* gap: 16px; */
/* justify-content: space-around;
} */

.buttoncontainer:is(.Mobile) {
  /* flex-wrap: wrap;
  display: flex;
  justify-content: space-between; */
  align-items: center;
  padding: 0;
}

.sub_buttoncontainer {
  display: flex;
  align-items: center;
  gap: 16px;
}

.right-button {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: none;
  background-image: var(
    --gradient-set-active,
    linear-gradient(120deg, #7653ff -4.72%, #8e4eff -3.32%, #ea27c2 45.42%)
  );
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  flex: 1;
  margin-bottom: 16px;
}

.right-button.retry {
  max-width: none;
}

/* .right-button:disabled {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: none;
  background: #dedede;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
} */

.progressContainer:is(.Mobile) {
  padding: 4%;
}

.bottom_line {
  border-bottom: 1px solid #e4e4e4;
}

.start_logo {
  width: 100%;
}

.sub_container {
  text-align: left;
  max-width: 580px;
  margin: auto;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0px 12px 24px 0px rgba(0, 0, 0, 0.24);
}

.sub_container:is(.Mobile) {
  padding: 16px;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 110%;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  will-change: transform, opacity;
  transform: translateZ(0);
  overflow-y: hidden;
  transition: top 0.3s;
}
.loading {
  margin-bottom: 0;
  color: var(--Web-Black-High, #212121);
  text-align: center;

  /* H2 */
  font-family: Outfit;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 26px */
}
.spinner-border {
  width: 5rem;
  height: 5rem;
}
.flex-container {
  margin: auto;
}

.retry p {
  color: var(--Web-Black-High, #212121);
  text-align: center;

  /* Body (Regular) */
  font-family: Outfit;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
}

.retry_logo {
  width: 80px;
  height: 80px;
  display: flex;
  margin: auto;
}

.inputText {
  margin-bottom: 14px;
  width: 100%;
}

.left-button {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: 1px solid #cb03bc;
  background-color: white;
  font-size: 14px;
  font-weight: 500;
  color: #cb03bc;
  flex: 1;
  text-align: center;
}

.left-button:is(.Mobile),
.right-button:is(.Mobile) {
  max-width: none;
}

.left-button:disabled,
.right-button:disabled {
  border: none;
  background: #dedede;
  color: #fff;
}

@media (max-width: 820px) {
  .buttoncontainer {
    flex-flow: column-reverse;
    gap: 12px;
  }

  .left-button,
  .right-button {
    max-width: none;
  }
}

@media (max-width: 620px) {
  .sub_buttoncontainer {
    display: grid;
  }
}
</style>
